import * as THREE from 'three';
import { makeRng } from '../core/Rand.js';
import { WeaponBuilder } from './parts/Builder.js';
import { buildRifle } from './parts/Rifle.js';
import { buildSMG, buildShotgun, buildPistol } from './parts/Guns2.js';
import { buildHandsFor } from './parts/Hands.js';

/**
 * Per-weapon data. Poses are transforms of the weapon origin in *view-camera space*,
 * so `hip.pos` is literally where the rear of the receiver sits relative to the eye.
 *
 * `class` is the audio/UI-facing weapon class and is exactly one of
 * `'rifle' | 'smg' | 'shotgun' | 'pistol' | 'sniper'`. It is published verbatim on
 * every `weapon:*` event (see `WeaponInstance.info()`), so audio can index its
 * gunshot bank from it directly.
 *
 * `suppressed` is the muzzle-device state and is likewise published on every
 * event; audio picks the suppressed gunshot variant from it. It is a per-instance
 * value at runtime (`ctx.weapons.setSuppressed(on)`), seeded from this default.
 *
 * `spread` numbers are CONE HALF-ANGLES in radians, matching what is published on
 * `ctx.weapons.spread` and what the UI crosshair consumes.
 *
 * `hip.pos.z` is how far the receiver sits in front of the eye. At 21 cm a rifle
 * subtends so much of a 60 degree viewmodel frustum that the muzzle leaves the
 * frame entirely and the weapon stops reading as a whole object — the classic
 * "gun is enormous and jammed into the corner" tell. Every weapon now sits at
 * 27-33 cm, which puts the muzzle back in shot without giving up the lower-right
 * screen real estate a COD viewmodel occupies.
 */

export const DEFS = {
  rifle: {
    label: 'M4A1',
    slot: 1,
    class: 'rifle',
    suppressed: false,
    weight: 1.0,
    magSize: 30, reserve: 210,
    fireModes: ['auto', 'burst', 'semi'],
    rpm: 780, burstCount: 3, burstGap: 0.24,
    hip: { pos: [0.0930, -0.0815, -0.2950], rot: [0.030, -0.080, 0.028] },
    sprint: { pos: [0.1420, -0.1420, -0.1750], rot: [-0.300, -0.640, 0.560] },
    tacSprint: { pos: [0.0900, -0.2050, -0.1250], rot: [-0.720, -0.980, 0.980] },
    // Eye to the rear lens of a micro red dot. At the previous 0.198 m the 26 mm
    // window subtended 3.8 degrees and the sight picture was a peephole with the
    // folded front sight filling its lower third — which is what the critique read
    // as "the scope is rendering the viewmodel's own geometry".
    eyeRelief: 0.1620, adsRoll: 0.0, adsFov: 47, adsTime: 0.22, sprintTime: 0.20,
    drawTime: 0.52, holsterTime: 0.28,
    reloadTime: 2.10, reloadEmptyTime: 2.95, magDropDistance: 0.36, magHandTravel: 0.32,
    meleeTime: 0.68, meleeStyle: 'stock', inspectTime: 2.60,
    swayScale: 1.0, bobScale: 1.0,
    kickBack: 0.028, kickPitch: 0.85, kickYaw: 0.50, kickRoll: 0.45, kickLateral: 0.9,
    recoil: {
      vertical: 0.00600, horizontal: 0.00220, vertMin: 0.42, rampShots: 2.4,
      hPhase: 1.31, hDrift: 0.55, vertRandom: 0.055, horizRandom: 0.28,
      visualScale: 2.6, snap: 34, retain: 0.22, recoverOmega: 11,
      settleDelay: 0.28, restDecay: 2.4,
    },
    spread: {
      spreadHip: 0.0300, spreadAds: 0.0016, spreadMove: 0.0280, spreadCrouch: 0.74,
      spreadAir: 2.1, spreadBloom: 0.0480, bloomPerShot: 0.115, bloomDecay: 0.85, bloomMax: 1,
    },
    ballistics: {
      damage: 33, falloffStart: 24, falloffEnd: 48, minDamageMul: 0.62,
      maxRange: 300, penetration: 26, maxPenetrations: 2, tracerSpeed: 640,
    },
    rig: {
      bolt: { bone: 'bolt', travel: 0.0850 },
      charging: { bone: 'charging', travel: 0.0850 },
      dust: { bone: 'dust', angle: -1.55 },
      mag: 'mag',
      trigger: { bone: 'trigger', angle: 0.26 },
      magRelease: { bone: 'magrelease', travel: -0.0035 },
      boltCatch: { bone: 'boltcatch', travel: 0.0035 },
      selector: { bone: 'selector', angle: 0.55 },
    },
    cycleTime: 0.055,
    build: buildRifle,
  },

  smg: {
    label: 'MP5A3',
    slot: 2,
    class: 'smg',
    suppressed: false,
    weight: 0.78,
    magSize: 30, reserve: 240,
    fireModes: ['auto', 'burst', 'semi'],
    rpm: 880, burstCount: 3, burstGap: 0.22,
    hip: { pos: [0.0905, -0.0775, -0.2680], rot: [0.034, -0.084, 0.030] },
    sprint: { pos: [0.1380, -0.1360, -0.1580], rot: [-0.300, -0.660, 0.580] },
    tacSprint: { pos: [0.0880, -0.1980, -0.1150], rot: [-0.740, -1.000, 1.000] },
    eyeRelief: 0.3950, adsFov: 47, adsTime: 0.18, sprintTime: 0.17,
    drawTime: 0.44, holsterTime: 0.24,
    reloadTime: 1.95, reloadEmptyTime: 2.70, magDropDistance: 0.32, magHandTravel: 0.28,
    meleeTime: 0.62, meleeStyle: 'stock', inspectTime: 2.40,
    swayScale: 0.88, bobScale: 0.95,
    kickBack: 0.022, kickPitch: 0.72, kickYaw: 0.46, kickRoll: 0.40, kickLateral: 1.0,
    recoil: {
      vertical: 0.00420, horizontal: 0.00250, vertMin: 0.50, rampShots: 2.0,
      hPhase: 2.77, hDrift: -0.35, vertRandom: 0.070, horizRandom: 0.34,
      visualScale: 2.4, snap: 31, retain: 0.18, recoverOmega: 12.5,
      settleDelay: 0.24, restDecay: 2.8,
    },
    spread: {
      spreadHip: 0.0265, spreadAds: 0.0026, spreadMove: 0.0230, spreadCrouch: 0.76,
      spreadAir: 2.0, spreadBloom: 0.0420, bloomPerShot: 0.095, bloomDecay: 0.95, bloomMax: 1,
    },
    ballistics: {
      damage: 26, falloffStart: 14, falloffEnd: 32, minDamageMul: 0.52,
      maxRange: 200, penetration: 14, maxPenetrations: 1, tracerSpeed: 520,
    },
    rig: {
      bolt: { bone: 'bolt', travel: 0.0620 },
      charging: { bone: 'charging', travel: 0.0620 },
      mag: 'mag',
      trigger: { bone: 'trigger', angle: 0.26 },
      magRelease: { bone: 'magrelease', travel: 0.0030 },
      boltCatch: { bone: 'charging', travel: 0.0000 },
      selector: { bone: 'selector', angle: 0.55 },
    },
    cycleTime: 0.048,
    build: buildSMG,
  },

  shotgun: {
    label: 'M870',
    slot: 3,
    class: 'shotgun',
    suppressed: false,
    weight: 1.35,
    magSize: 6, reserve: 42,
    fireModes: ['pump'],
    rpm: 78, pellets: 9,
    hip: { pos: [0.0985, -0.0875, -0.3150], rot: [0.028, -0.078, 0.026] },
    sprint: { pos: [0.1500, -0.1500, -0.1900], rot: [-0.290, -0.620, 0.540] },
    tacSprint: { pos: [0.0960, -0.2150, -0.1350], rot: [-0.700, -0.960, 0.960] },
    eyeRelief: 0.7900, adsFov: 50, adsTime: 0.28, sprintTime: 0.24,
    drawTime: 0.60, holsterTime: 0.32,
    pumpTime: 0.64, shellLoadTime: 0.46,
    meleeTime: 0.72, meleeStyle: 'stock', inspectTime: 2.60,
    swayScale: 1.20, bobScale: 1.10,
    kickBack: 0.055, kickPitch: 1.35, kickYaw: 0.55, kickRoll: 0.60, kickLateral: 0.8,
    recoil: {
      vertical: 0.02600, horizontal: 0.00520, vertMin: 0.85, rampShots: 1.2,
      hPhase: 0.53, hDrift: 0.15, vertRandom: 0.120, horizRandom: 0.55,
      visualScale: 2.2, snap: 40, retain: 0.30, recoverOmega: 8.5,
      settleDelay: 0.20, restDecay: 3.2,
    },
    spread: {
      spreadHip: 0.0620, spreadAds: 0.0380, spreadMove: 0.0250, spreadCrouch: 0.82,
      spreadAir: 1.6, spreadBloom: 0.0300, bloomPerShot: 0.35, bloomDecay: 1.4, bloomMax: 1,
    },
    ballistics: {
      damage: 17, falloffStart: 6, falloffEnd: 18, minDamageMul: 0.22,
      maxRange: 60, penetration: 8, maxPenetrations: 1, tracerSpeed: 300,
    },
    rig: {
      bolt: { bone: 'bolt', travel: 0.0620 },
      pump: { bone: 'pump', travel: 0.0780 },
      trigger: { bone: 'trigger', angle: 0.24 },
    },
    cycleTime: 0.10,
    build: buildShotgun,
  },

  pistol: {
    label: 'G17',
    slot: 4,
    class: 'pistol',
    suppressed: false,
    weight: 0.55,
    magSize: 17, reserve: 68,
    fireModes: ['semi'],
    rpm: 430,
    hip: { pos: [0.0810, -0.0760, -0.3250], rot: [0.036, -0.088, 0.030] },
    sprint: { pos: [0.1250, -0.1300, -0.2050], rot: [-0.320, -0.700, 0.600] },
    tacSprint: { pos: [0.0800, -0.1900, -0.1600], rot: [-0.760, -1.020, 1.020] },
    eyeRelief: 0.5000, adsFov: 48, adsTime: 0.15, sprintTime: 0.15,
    drawTime: 0.34, holsterTime: 0.20,
    reloadTime: 1.72, reloadEmptyTime: 2.35, magDropDistance: 0.30, magHandTravel: 0.26,
    meleeTime: 0.56, meleeStyle: 'pistol', inspectTime: 2.20,
    swayScale: 0.72, bobScale: 0.85,
    kickBack: 0.030, kickPitch: 1.05, kickYaw: 0.45, kickRoll: 0.40, kickLateral: 0.9,
    recoil: {
      vertical: 0.01250, horizontal: 0.00280, vertMin: 0.90, rampShots: 1.0,
      hPhase: 4.19, hDrift: 0.10, vertRandom: 0.090, horizRandom: 0.45,
      visualScale: 2.2, snap: 38, retain: 0.16, recoverOmega: 14,
      settleDelay: 0.18, restDecay: 4.0,
    },
    spread: {
      spreadHip: 0.0330, spreadAds: 0.0022, spreadMove: 0.0260, spreadCrouch: 0.74,
      spreadAir: 2.2, spreadBloom: 0.0520, bloomPerShot: 0.20, bloomDecay: 1.6, bloomMax: 1,
    },
    ballistics: {
      damage: 31, falloffStart: 12, falloffEnd: 28, minDamageMul: 0.48,
      maxRange: 160, penetration: 11, maxPenetrations: 1, tracerSpeed: 380,
    },
    rig: {
      bolt: { bone: 'slide', travel: 0.0300 },
      mag: 'mag',
      trigger: { bone: 'trigger', angle: 0.22 },
      magRelease: { bone: 'magrelease', travel: -0.0030 },
      boltCatch: { bone: 'slidestop', travel: 0.0025 },
    },
    cycleTime: 0.075,
    build: buildPistol,
  },
};

/**
 * Build a weapon: geometry, hands solved onto its grips, then merged down to a
 * handful of SkinnedMeshes sharing one skeleton.
 */
export function buildWeapon(name, materialFor, seed = 0xB1ADE) {
  const def = DEFS[name];
  if (!def) throw new Error(`Unknown weapon "${name}"`);
  const rng = makeRng(seed ^ (name.length * 0x9e37));
  const B = new WeaponBuilder({ uvScale: 64, wearScale: 0.34 });
  const meta = def.build(B, rng);

  const hands = meta.handPoses || defaultHandPoses(meta);
  meta.hands = buildHandsFor(B, rng, hands);
  // Solved in `Hands.buildHand` against the actual blade anchor: the per-joint
  // curl the runtime must add to the bind pose for the pad to touch the trigger.
  meta.triggerCurl = meta.hands.right?.triggerCurl ?? [0.62, 0.72, 0.46];
  meta.indexDistal = meta.hands.right?.indexDistal ?? 0.0228;

  const built = B.build(materialFor);
  built.triangles = 0;
  return { def, meta, built };
}

/**
 * The hand solver only needs, per hand: which cylinder to wrap, where on that cylinder
 * the heel of the palm starts, and which way the forearm runs. Everything else — palm
 * curvature, knuckle position, every finger joint — falls out of the wrap.
 */
function defaultHandPoses(meta) {
  const a = meta.anchors;
  // 0 disables the C-clamp (the pistol, whose support "cylinder" is the firing
  // hand); the axial root offset only means anything when it is on.
  const leftAxial = a.leftThumbAxial ?? 0.72;
  return {
    right: {
      cyl: a.rightGrip,
      heelRadial: a.rightRadial ?? [0.58, 0.30, 0.76],
      along: a.rightAlong ?? -0.030,
      /*
       * The forearm runs STEEPLY down now, and this is the second half of the
       * "dark sliver across the receiver" fix.
       *
       * Picked by raycast at `wpn_dbg_grip` (870,255) and (920,285): the thin
       * unlit band crossing the receiver is `wpn_webbing` / `wpn_glove` on
       * `right_hand` at weapon y = -0.005 — the top of the glove GAUNTLET and
       * its cuff strap, 13 mm above the top edge of the lower receiver. It is
       * the WRIST, not the thumb; the thumb was a second, separate instance of
       * the same class of error and is fixed at `thumbYaw` below.
       *
       * Dropping the whole hand down the grip was tried first and is wrong: at
       * `along = -0.014` it took the trigger-pressed contact from 0.00 mm to
       * 2.85 mm, because the trigger IK is solved from the MCP and 16 mm is
       * more reach than the finger has spare. The forearm and the cuff are
       * decorative (`occlude: false`, not in the contact test), so they are
       * the free parameters: [0.30, -0.62, 0.72] rakes the wrist below the
       * receiver line without touching a single solved contact.
       */
      forearmDir: a.rightForearm ?? [0.30, -0.62, 0.72],
      /*
       * Per-segment wrap limits for the palm.
       *
       * These were 0.62/0.58/0.50 rad, which is less than the angle a 28-34 mm
       * palm segment subtends on a 34 mm grip circle — so the wrap hit its limit
       * on every segment and the palm flew off tangentially, landing the MCP row
       * 26 mm in FRONT of the grip and effectively on top of the trigger. That is
       * why no amount of index-finger curl could put the pad on the blade.
       */
      palmLimits: a.rightPalmLimits ?? [0.88, 1.02, 0.92],
      // Terminate the palm loft at the web of the hand instead of letting it
      // stand 19 mm proud of the receiver. See `Hands.buildHand :: webTrim`.
      webTrim: a.rightWebTrim ?? 0.55,
      trigger: true,
      triggerTarget: a.trigger ?? null,
      /*
       * THE DARK SLIVER ACROSS THE RECEIVER — measured, not guessed.
       *
       * `thumbYaw` tilts the thumb's start direction out of the wrap plane by
       * rotating it about the grip's RADIAL, so it is the one parameter that
       * decides how far the thumb travels ALONG the grip axis. At 0.62 rad a
       * 78 mm thumb climbs sin(0.62) x 78 = 45 mm up the grip. Measured joint
       * positions with that value:
       *
       *   right_thumb0  ( 0.0246, -0.0592,  0.0101)
       *   right_thumb1  ( 0.0126, -0.0533, -0.0072)
       *   right_thumb2  (-0.0119, -0.0397, -0.0212)
       *   glove vertices bound to the thumb reach y = -0.0175, x = -0.0402
       *
       * The lower receiver's underside is y = -0.0178 and its flank is
       * x = +/-0.0192 — so the tip finished level with the bottom of the
       * receiver and 21 mm PAST its left flank, sticking out of the
       * silhouette. Seen from the right-hand cameras it is a thin unlit ribbon
       * crossing the receiver, which is exactly what the reviewer reports in
       * `wpn_hands` and `wpn_dbg_grip`.
       *
       * Capping the wrap limits was tried first and is wrong: `wrapChain`
       * bisects for the angle that lands each joint on the grip surface, so a
       * cap below that angle leaves the thumb floating — measured, it took the
       * worst grip contact from 0.04 mm to 6.09 mm. The axial climb is the
       * defect; 0.20 rad puts the tip ~14 mm up the grip instead of 45, i.e.
       * three centimetres clear of the receiver, and the wrap solver still
       * lands every joint at exactly `radius + halfThickness`.
       */
      thumbYaw: a.rightThumbYaw ?? 0.20,
      thumbLift: 0.14,
      thumbAlong: 0.62,
      /*
       * The thumb's root offset ALONG the grip axis, as a fraction of palm
       * width. It was -0.42 (the default), which is 34 mm — and the palm heel
       * sits only 22 mm below the receiver, so the thumb started 12 mm above
       * the top of the grip and swept a 94 mm digit up through the roof of the
       * gun. That is the "black spikes" the reviewer found at `wpn_closeup`
       * (1350,270) and (1400,230); a material-ID render identifies them as
       * `glove`. A real thumb's MCP sits level with the index MCP, which on a
       * 106 mm grip is about a third of this.
       */
      thumbAxialFrac: a.rightThumbAxial ?? 0.06,
    },
    left: {
      cyl: a.leftGrip,
      heelRadial: a.leftRadial ?? [-0.62, -0.79, 0.0],
      along: a.leftAlong ?? 0.026,
      forearmDir: a.leftForearm ?? [-0.30, -0.72, 0.62],
      palmLimits: a.leftPalmLimits ?? [0.80, 0.94, 0.86],
      thumbYaw: a.leftThumbYaw ?? 0.55,
      thumbLift: 0.16,
      thumbAlong: 0.70,
      /*
       * Support thumb: C-clamp, laid along the top-left of the handguard and
       * pointing at the muzzle, instead of wrapped 34 mm REARWARD of the palm
       * where the palm mass hid it completely — "a four-cylinder mitten with
       * no thumb". `thumbAxial` roots it on the tube surface, so every joint
       * still lands at exactly `radius + halfThickness` and the measured grip
       * contact is unchanged.
       */
      thumbAxial: leftAxial,
      thumbAxialFrac: leftAxial > 0 ? (a.leftThumbFrac ?? -0.34) : undefined,
      /*
       * 0.45 rad put the support thumb on the LEFT FLANK of the handguard,
       * level with the palm — so from every camera the palm mass sits in front
       * of it and it reads as part of the glove rather than as a thumb. A
       * modern C-clamp lays the thumb over the TOP-LEFT SHOULDER of the tube,
       * where it silhouettes against the handguard instead of against the hand.
       * 0.95 rad rotates the root 41 degrees above the horizontal; `wrapChain`
       * still lands every joint at `radius + halfThickness`, so the measured
       * left-thumb contact is unchanged.
       */
      thumbAzimuth: a.leftThumbAzimuth ?? 0.95,
    },
  };
}
